import { useState, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Badge, Button, DataTable, EmptyState, Heading, SearchInput, Text, tabularNumsClass, type DataTableColumn } from "@lendcord/concord";
import { useListLoanSchemesQuery } from "../api/loanSchemes.api";
import type { LoanScheme } from "../setup.types";
import { LoanSchemeFormDialog } from "./LoanSchemeFormDialog";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function EditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function formatLoanRange(scheme: LoanScheme): string {
  const max = currencyFormatter.format(scheme.maxLoanAmount);
  if (scheme.minLoanAmount == null) return `Up to ${max}`;
  return `${currencyFormatter.format(scheme.minLoanAmount)} – ${max}`;
}

export function LoanSchemesTab() {
  const intl = useIntl();
  const { data: schemes, isLoading } = useListLoanSchemesQuery();
  const [query, setQuery] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState<LoanScheme | null>(null);

  const filtered = schemes
    ? schemes.filter((scheme) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return scheme.schemeCode.toLowerCase().includes(q) || scheme.name.toLowerCase().includes(q);
      })
    : null;

  const columns: DataTableColumn<LoanScheme>[] = [
    {
      id: "schemeCode",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnSchemeCode" }),
      cell: (scheme) => <span className={`font-medium ${tabularNumsClass}`}>{scheme.schemeCode}</span>,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "name",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnName" }),
      cell: (scheme) => scheme.name,
      cellClassName: "whitespace-nowrap font-medium text-text",
    },
    {
      id: "loanRange",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnLoanRange" }),
      cell: (scheme) => formatLoanRange(scheme),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "interestRate",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnInterestRate" }),
      cell: (scheme) => `${scheme.annualInterestRate}%`,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "emiType",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnEmiType" }),
      cell: (scheme) => intl.formatMessage({ id: scheme.emiType === "FLAT" ? "lender.loanSchemes.emiTypeFlat" : "lender.loanSchemes.emiTypeReducingBalance" }),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "status",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnStatus" }),
      cell: (scheme) => (
        <Badge status={scheme.isActive ? "success" : "info"}>
          <FormattedMessage id={scheme.isActive ? "lender.loanSchemes.statusActive" : "lender.loanSchemes.statusInactive"} />
        </Badge>
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.loanSchemes.columnAction" }),
      cell: (scheme) => (
        <button
          type="button"
          aria-label={intl.formatMessage({ id: "lender.loanSchemes.actionEdit" })}
          onClick={() => setEditingScheme(scheme)}
          className="inline-flex items-center justify-center text-text-muted hover:text-accent-600"
        >
          <EditIcon className="h-4 w-4" />
        </button>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>
          <FormattedMessage id="lender.loanSchemes.heading" />
        </Heading>
        <Button type="button" onClick={() => setIsAddOpen(true)}>
          <FormattedMessage id="lender.loanSchemes.addButton" />
        </Button>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          placeholder={intl.formatMessage({ id: "lender.loanSchemes.searchPlaceholder" })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered ?? []}
        rowKey={(scheme) => scheme.id}
        loading={isLoading}
        titleColumnId="name"
        metaColumnId="status"
        emptyState={
          <EmptyState>
            <FormattedMessage id={query ? "lender.loanSchemes.noMatchingSchemesMessage" : "lender.loanSchemes.emptyStateMessage"} />
          </EmptyState>
        }
      />

      <LoanSchemeFormDialog open={isAddOpen} onOpenChange={setIsAddOpen} />
      {editingScheme && (
        <LoanSchemeFormDialog open={editingScheme !== null} onOpenChange={() => setEditingScheme(null)} scheme={editingScheme} />
      )}
    </div>
  );
}
